import React, { useState } from 'react';

import { Link } from '~/lib/react-router-dom';

import {
  Alert,
  Box,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';

import { useRewardsPools } from '~/hooks/apis/rewards/useSocieteReward';

import { flashRewardsOfferUrl } from './flashRewardsUrl';

/** Fixed page size for the pools table — `TablePagination`'s rows-per-page control is hidden. */
const PAGE_SIZE = 20;

/**
 * Admin, read-only listing of every Société flash-hotel reward pool — "Pools" under the Flash
 * Rewards tab group. Pools are created lazily (on an offer's first reservation or its first cap
 * write), so an admin can't simply browse offers to find every pool with a cap or activity; this
 * tab is the answer to "where do I see all the offers I've capped".
 *
 * Pagination is server-driven: `page` state maps to `offset = page * PAGE_SIZE` into
 * `useRewardsPools`, which the hook re-fetches per page. Rows-per-page is fixed at 20 (not
 * user-adjustable), so `TablePagination`'s rows-per-page selector is effectively hidden by passing
 * it a single option.
 *
 * Loyalty doesn't know offer names, so the Offer column shows the raw offer ID with a link into
 * the Offers sub-tab rather than fetching and displaying a name (v1 design).
 */
export default function FlashRewardsPoolsPage() {
  const [page, setPage] = useState(0);
  const offset = page * PAGE_SIZE;

  const poolsQuery = useRewardsPools({ limit: PAGE_SIZE, offset });
  const rows = poolsQuery.data?.pools ?? [];
  const total = poolsQuery.data?.total ?? 0;

  return (
    <Stack spacing={3} sx={{ py: 3 }}>
      <Typography variant="h5">Pools</Typography>
      <Typography variant="body2" color="text.secondary">
        Every Société flash-hotel reward pool, newest-updated first.
      </Typography>

      {poolsQuery.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {poolsQuery.isError && (
        <Alert severity="error">
          {poolsQuery.error instanceof Error ? poolsQuery.error.message : 'Failed to load pools.'}
        </Alert>
      )}

      {poolsQuery.isSuccess && total === 0 && (
        <Typography variant="body2" color="text.secondary" py={4} textAlign="center">
          No pools yet — pools are created when an offer gets its first reservation or an admin sets a cap.
        </Typography>
      )}

      {poolsQuery.isSuccess && total > 0 && (
        <>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Offer</TableCell>
                <TableCell>Cap</TableCell>
                <TableCell>Reserved</TableCell>
                <TableCell>Redeemed</TableCell>
                <TableCell>Remaining</TableCell>
                <TableCell>Last updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.offerId}>
                  <TableCell>
                    <Stack spacing={0}>
                      <Typography variant="body2">{row.offerId}</Typography>
                      <Link to={flashRewardsOfferUrl(row.offerId)}>Open in Offers</Link>
                    </Stack>
                  </TableCell>
                  <TableCell>{row.totalQuantity ?? 'Default'}</TableCell>
                  <TableCell>{row.reservedCount}</TableCell>
                  <TableCell>{row.redeemedCount}</TableCell>
                  <TableCell>{row.remaining}</TableCell>
                  <TableCell>{new Date(row.updatedAt).toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_event, newPage) => setPage(newPage)}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
            onRowsPerPageChange={() => {
              // Rows-per-page is fixed — the selector is hidden since rowsPerPageOptions has a
              // single entry, so this is never actually invoked.
            }}
          />
        </>
      )}
    </Stack>
  );
}
